/**
 * Centralized table column configuration for different item types
 * 
 * This module provides item-type-specific column definitions and field metadata
 * for use in table displays (FolderGroupedTableDisplay, TemplateGroupedTableDisplay, etc.)
 */

import { FieldMetadata } from '../types';
import { objectsDataSource, OBJECTS_TABLE_COLUMNS } from '../sources/objects';
import { documentsDataSource, DOCUMENTS_TABLE_COLUMNS } from '../sources/documents';
import { templatesDataSource, TEMPLATES_TABLE_COLUMNS } from '../sources/templates';
import { variablesDataSource, VARIABLES_TABLE_COLUMNS } from '../sources/variables';

// Dialog columns - dialogs don't have a full DataSource yet, so define inline
const DIALOGS_TABLE_COLUMNS = ["Name", "Dialog Key", "Description", "Tags", "Status", "Last Update"];

// Dialog field metadata
const dialogFields: Record<string, FieldMetadata> = {
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
  },
  dialog_key: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
  },
  description: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  tags: {
    type: 'array',
    editable: 'dropdown',
    filterable: true,
    sortable: false,
    linkedDataSource: 'tags',
    multiSelect: true,
    displayField: 'name',
    valueField: 'id',
  },
  status: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'statuses',
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
  },
};

/**
 * Column type for UI display
 */
export type ColumnType = 'metadata' | 'section' | 'field';

export interface ColumnOption {
  id: string;
  label: string;
  type: ColumnType;
  sectionId?: string;
}

/**
 * Map column labels to their IDs for each item type
 * Handles special cases like camelCase IDs
 */
const columnIdMaps: Record<string, Record<string, string>> = {
  library: {
    'Name': 'name',
    'Type': 'type',
    'Owner': 'owner',
    'Tags': 'tags',
    'Status': 'status',
    'Last Update': 'lastUpdate',
    'Folder': 'folder',
    'Identifiers': 'identifiers',
    'Blueprint': 'blueprint',
    'Blueprint Version': 'blueprint_version',
    'Version': 'version',
  },
  documents: {
    'Title': 'title',
    'Description': 'description',
    'Owner': 'owner',
    'Tags': 'tags',
    'Status': 'status',
    'Last Update': 'lastUpdate',
  },
  blueprints: {
    'Name': 'name',
    'Description': 'description',
    'Owner': 'owner',
    'Tags': 'tags',
    'Last Update': 'lastUpdate',
  },
  dialogs: {
    'Name': 'name',
    'Dialog Key': 'dialog_key',
    'Description': 'description',
    'Tags': 'tags',
    'Status': 'status',
    'Last Update': 'lastUpdate',
  },
  variables: {
    'Name': 'name',
    'Type': 'element_type',
    'Group': 'group_name',
    'Values': 'data',
    'Folder': 'folder',
    'Description': 'description',
    'Last Update': 'lastUpdate',
  },
};

/**
 * Get the table columns for a specific item type
 */
export function getTableColumnsForItemType(itemType: string): string[] {
  let columns: string[];
  
  switch (itemType) {
    case 'library':
      columns = OBJECTS_TABLE_COLUMNS;
      break;
    case 'documents':
      columns = DOCUMENTS_TABLE_COLUMNS;
      break;
    case 'blueprints':
      columns = TEMPLATES_TABLE_COLUMNS;
      break;
    case 'dialogs':
      columns = DIALOGS_TABLE_COLUMNS;
      break;
    case 'variables':
      columns = VARIABLES_TABLE_COLUMNS;
      break;
    default:
      columns = OBJECTS_TABLE_COLUMNS;
  }
  
  // For library items, exclude Type, Tags, and Identifiers from table columns
  if (itemType === 'library') {
    const excludedColumns = ['Type', 'Tags', 'Identifiers'];
    return columns.filter(col => !excludedColumns.includes(col));
  }
  
  return columns;
}

/**
 * Get the field metadata for a specific item type
 */
export function getFieldMetadataForItemType(itemType: string): Record<string, FieldMetadata> {
  switch (itemType) {
    case 'library':
      return objectsDataSource.fields;
    case 'documents':
      return documentsDataSource.fields;
    case 'blueprints':
      return templatesDataSource.fields;
    case 'dialogs':
      return dialogFields;
    case 'variables':
      return variablesDataSource.fields;
    default:
      return objectsDataSource.fields;
  }
}

/**
 * Get column options formatted for UI display (TableColumnSelector)
 */
export function getMetadataColumnsForItemType(itemType: string): ColumnOption[] {
  const columns = getTableColumnsForItemType(itemType);
  const idMap = columnIdMaps[itemType] || columnIdMaps.library;
  
  // For library items, exclude Type, Tags, and Identifiers from metadata columns in table mode
  const excludedLibraryColumns = ['Type', 'Tags', 'Identifiers'];
  const filteredColumns = itemType === 'library' 
    ? columns.filter(col => !excludedLibraryColumns.includes(col))
    : columns;
  
  return filteredColumns.map(col => ({
    id: idMap[col] || col.toLowerCase().replace(/\s+/g, '_'),
    label: col,
    type: 'metadata' as ColumnType,
  }));
}

/**
 * Get default selected column IDs for a specific item type
 */
export function getDefaultColumnsForItemType(itemType: string): string[] {
  return getMetadataColumnsForItemType(itemType).map(col => col.id);
}

